import querystring from 'query-string';
import _orderBy from 'lodash/orderBy';
import { checkIsGameHasPromoOptions, checkIsGameHasPromoTask } from 'config/promoGameTypes';
import {
    MOCK_API_ENDPOINT,
    FEVER_API_ENDPOINT,
    EDITOR_API_FEVER_HOST,
    IMG_UPLOAD_API_PATH
} from 'config/envVariable';
import * as formTypes from 'config/formTypes';
import permissionsFunctionMapping, {
    permissionsRouteMapping
} from 'config/permissionsFunctionMapping';
import {
    GET,
    POST,
    PATCH,
    PUT,
    DELETE,
    normalizeResponse,
    normalizeBlobResponse
} from 'api/common';
import { objFirstLevelJSONParse, isJSONParsable } from 'utils/objectManipulate';
import parseAnimationSettings from 'utils/parseAnimationSettings';
import { contestGetUgcLinkKey } from 'config/promoGameTypes';
import { passcodeTypes } from 'config/passcode';
import { getFirebaseIdToken, initFirebaseLogin } from 'editor/utils/firebaseManager';
import { getZinesPageInfo } from 'api/editorZines';
import { normalizePromoTasksList, canNotRetrieveFormData, preProcessAwardeeForm } from 'api/utils';
import { getPointPools } from 'api/point/editor';
import { getMgmRelationByEditorApi } from 'api/mgm/editor';
import { isEmptyArray } from 'utils/manipulateArray';
import {
    filterNoDepositGlobalPool,
    filterNoWithdrawDepositGlobalPool,
    filterNoWithdrawGlobalPool
} from 'utils/pointPoolPermission';
import { filterUndefinedOrNull } from 'utils/filter';
import { JOIN_AND_GET } from 'config/promoLotteryTypes';
import { IS_OFFLINE } from 'config/promoOnlineStatus';
import { taskTypes, taskSyncTypes } from 'config/task';
import { OPTIONS_TYPE } from 'config/formBasedSectionTypes';

export async function fetchPromo(
    path,
    optionsPromise,
    {
        dataKeys = 'data',
        strictErrThrow = true,
        passResponse = false,
        customErrorHandler,
        isMock = false
    } = {}
) {
    const options = await optionsPromise;
    const baseUrl = isMock ? MOCK_API_ENDPOINT : FEVER_API_ENDPOINT;
    return fetch(`${baseUrl}${path}`, options)
        .then(normalizeResponse)
        .then(resp => {
            const generalErrorHandler = () => {
                if (Boolean(passResponse)) {
                    return resp;
                }
                if (!strictErrThrow) {
                    return {
                        ...resp,
                        payload: resp.payload[dataKeys]
                    };
                }
                if (resp.httpStatus >= 400) {
                    throw resp;
                }
                return resp.payload[dataKeys];
            };

            if (typeof customErrorHandler === 'function') {
                return customErrorHandler({ resp, dataKeys, generalErrorHandler });
            }
            return generalErrorHandler();
        });
}

async function fetchPromoBlob(path, optionsPromise) {
    const options = await optionsPromise;
    return fetch(`${FEVER_API_ENDPOINT}${path}`, options).then(resp => {
        if (resp.status !== 200) {
            throw resp;
        }
        return normalizeBlobResponse(resp);
    });
}

export async function generateFetchOpts({
    method = GET,
    headers = {},
    body = null,
    formData,
    apiVersion = 1,
    ...rest
} = {}) {
    return {
        method,
        headers: new Headers({
            'Fever-Host': EDITOR_API_FEVER_HOST,
            Accept: `application/x.api.v${apiVersion}+json`,
            Authorization: `Bearer ${await getFirebaseIdToken()}`,
            ...(body ? { 'Content-Type': 'application/json; charset=utf-8' } : {}),
            ...headers
        }),
        body: formData ? formData : body && JSON.stringify(body),
        ...rest
    };
}

export async function getEditorGeneralData(sponsorId) {
    await initFirebaseLogin();
    const [
        partner,
        partnerSetting,
        sponsor,
        sponsorSetting,
        plans,
        availPlanItems,
        account,
        accountRole
    ] = await Promise.all([
        getPartner(),
        getPartnerSetting(),
        getSponsor(sponsorId),
        getSponsorSetting(sponsorId),
        getSponsorPlans({ sponsorId }),
        getSponsorAvailPlanItems(sponsorId),
        getAccountInfo(),
        getAccountRoleInfo(sponsorId)
    ]);

    const [sponsorThirdParty = {}, partnerThirdParty = {}] = await Promise.all([
        getThirdPartyApi({
            sponsorId,
            level: THIRD_PARTY_LEVEL_TYPE.SPONSOR,
            levelUuid: sponsor.uuid
        }),
        getThirdPartyApi({
            sponsorId,
            level: THIRD_PARTY_LEVEL_TYPE.PARTNER,
            levelUuid: partner.uuid
        })
    ]);

    return {
        partner: {
            ...partner,
            ...partnerSetting,
            thirdParty: partnerThirdParty
        },
        sponsor: {
            ...sponsor,
            ...sponsorSetting,
            thirdParty: sponsorThirdParty,
            accountRole: {
                ...accountRole,
                permissionsFunction: permissionsFunctionMapping(accountRole.permissions),
                permissionsRoute: permissionsRouteMapping(accountRole.permissions)
            }
        },
        availPlanItems,
        plans,
        account
    };
}

export async function getEditorPromoData(sponsorId, promoId) {
    const [promo = {}, promoSetting = {}] = await Promise.all([
        getPromotion(sponsorId, promoId),
        getPromoSetting(sponsorId, promoId)
    ]);
    const { Games: { formId } = {}, Zines: { pageId } = {} } = promoSetting;
    const [
        planBinded,
        forms,
        zines,
        { lotteries, prizes },
        settingOption,
        promoTasks,
        pointPools,
        mgmRelation,
        awardeeFormList,
        aiExtensionConfigs
    ] = await Promise.all([
        getAvailablePlanListForPromo(sponsorId, promoId)
            .then(res => (isEmptyArray(res) ? {} : res[0]))
            .catch(() => ({})),
        getPromoForm(sponsorId, formId, promo.type),
        getPromoZines(sponsorId, pageId),
        getLotteriesAndPrize(sponsorId, promoId, promoSetting, promo.status),
        getPromoOption(sponsorId, promoId, promo.type),
        getPromoTasks(sponsorId, promoId, promo.type),
        getPointPools({ sponsorId, order: 'updated', status: 'usable' }),
        getPromoMgmRelationState({ sponsorId, promoId }),
        getAwardeeFormList(promoId, sponsorId),
        getAiExtensionConfigsApi()
    ]);

    let aiExtensions = {};
    if (aiExtensionConfigs?.promoTypeToAIExtensionMapping?.[promo.type]) {
        // 該活動模組有支援 AI 才呼叫
        aiExtensions = await getPromoAiExtensionsApi(sponsorId, promoId);
    }

    const filterNoWithdrawGlobalPools = filterNoWithdrawGlobalPool(pointPools) || [];
    const filterNoDepositGlobalPools = filterNoDepositGlobalPool(pointPools) || [];
    const filterNoWithdrawDepositGlobalPools = filterNoWithdrawDepositGlobalPool(pointPools) || [];
    // [86ep4yxnh] 舊的空 formbased 活動有位移除的 formId 在 promo setting 內，前端初始化時要過濾掉，避免觸發 submit form
    const filterFormIdSettingGame = filterUndefinedOrNull(
        canNotRetrieveFormData(formId, promo.type)
            ? { ...promoSetting.Games, formId: undefined }
            : promoSetting.Games
    );
    return {
        promo,
        settingGame: objFirstLevelJSONParse(filterFormIdSettingGame),
        settingGameStyle: objFirstLevelJSONParse(promoSetting.GameStyles),
        settingMGMReferral: objFirstLevelJSONParse(promoSetting.MGMReferral),
        settingEntryValue: objFirstLevelJSONParse(promoSetting.EntryValues),
        settingAIExtensions: objFirstLevelJSONParse(promoSetting.AIExtensions),
        settingPromo: promoSetting.Promotions,
        settingPointSystems: promoSetting.PointSystems,
        settingTheme: promoSetting.Themes,
        settingStyle: objFirstLevelJSONParse(promoSetting.Styles),
        signUpForm: promoSetting.SignUpForm,
        zines,
        lotteries,
        prizes,
        relatedWith: {},
        planBinded,
        pointSystem: {
            pointPools,
            filterNoWithdrawGlobalPools,
            filterNoDepositGlobalPools,
            filterNoWithdrawDepositGlobalPools
        },
        aiExtensionConfigs,
        awardeeForm: preProcessAwardeeForm(awardeeFormList, promoSetting?.AwardeeForms),
        aiExtensions,
        ...promoTasks,
        ...forms,
        ...settingOption,
        ...mgmRelation
    };
}

export async function getEditorMgmData(sponsorId, promoId, pointSystem) {
    const [promo = {}, promoSetting = {}] = await Promise.all([
        getPromotion(sponsorId, promoId),
        getPromoSetting(sponsorId, promoId)
    ]);
    const [{ lotteries, prizes }, promoTasks, awardeeFormList] = await Promise.all([
        getLotteriesAndPrize(sponsorId, promoId, promoSetting),
        getPromoTasks(sponsorId, promoId, promo.type),
        getAwardeeFormList(promoId, sponsorId)
    ]);

    return {
        promo,
        settingGame: objFirstLevelJSONParse(promoSetting.Games),
        lotteries,
        prizes,
        pointSystem,
        awardeeForm: preProcessAwardeeForm(awardeeFormList, promoSetting?.AwardeeForms),
        ...promoTasks
    };
}

export async function getPromoMgmRelationState({ sponsorId, promoId }) {
    const response = await getMgmRelationByEditorApi({ sponsorId, promoId });
    if (response.httpStatus === 226) {
        return {
            mgmRelation: {}
        };
    }

    return {
        mgmRelation: {
            ...response.payload
        }
    };
}

async function getEditorSecondaryPromoData(sponsorId, primaryPromoData) {
    const { promo: { type: gameType } = {}, settingPromo = {} } = primaryPromoData;
    const relatedPromoKey = contestGetUgcLinkKey(gameType, settingPromo);
    const relatedPromoUuid = settingPromo[relatedPromoKey];

    return Boolean(relatedPromoUuid) ? await getEditorPromoData(sponsorId, relatedPromoUuid) : null;
}

async function getEditorMgmPromoData(sponsorId, primaryPromoData) {
    const { mgmRelation: { mgmReferral, mgmRole } = {}, pointSystem } = primaryPromoData;

    return mgmRole === 'mgmShare' || mgmRole === 'mgmSelf'
        ? await getEditorMgmData(sponsorId, mgmReferral.id, pointSystem)
        : null;
}

export async function getEditorData(sponsorId, promoId) {
    const editorGeneralData = await getEditorGeneralData(sponsorId);
    const editorPrimaryPromoData = await getEditorPromoData(sponsorId, promoId);
    const editorSecondaryPromoData = await getEditorSecondaryPromoData(
        sponsorId,
        editorPrimaryPromoData
    );
    const editorMgmPromoData = await getEditorMgmPromoData(sponsorId, editorPrimaryPromoData);
    return {
        primary: { ...editorGeneralData, ...editorPrimaryPromoData },
        secondary: editorSecondaryPromoData && {
            ...editorGeneralData,
            ...editorSecondaryPromoData
        },
        mgm: editorMgmPromoData && {
            ...editorGeneralData,
            ...editorMgmPromoData
        }
    };
}

async function getPromoZines(sponsorId, pageId) {
    return !pageId ? {} : getZinesPageInfo(sponsorId, pageId);
}

async function getPromoOption(sponsorId, promoId, gameType) {
    if (!checkIsGameHasPromoOptions(gameType)) {
        return {};
    }
    const settingOption = await getPromoOptionListApi({ sponsorId, promotionId: promoId });

    return { settingOption };
}

async function getPromoTasks(sponsorId, promoId, gameType) {
    if (!checkIsGameHasPromoTask(gameType)) {
        return {};
    }

    const promoTasks = await normalizePromoTasksList(
        await getPromoTaskListApi({ sponsorId, promoId })
    );

    return { promoTasks };
}

async function getPromoForm(sponsorId, formId, promoType) {
    if (canNotRetrieveFormData(formId, promoType)) {
        return { pages: [], sections: [], fields: [], options: [], settings: {} };
    }

    const { pages, sections, fields, options, settings, form } = await getFormEditorData(
        sponsorId,
        formId
    );
    return { form, pages, sections, fields, options, settings };
}

async function getLotteriesAndPrize(sponsorId, promoId, promoSetting, promoStatus = IS_OFFLINE) {
    const {
        LotteryInstant: { uuid: lotteryInstantUuid } = {},
        LotteryParticipant: { uuid: lotteryParticipantUuid } = {},
        LotteryAdmin: { uuid: lotteryAdminUuid } = {},
        LotteryTasks: { uuid: lotteryTasks = {} } = {}
    } = promoSetting;

    const lotteryTasksParsed = isJSONParsable(lotteryTasks)
        ? JSON.parse(lotteryTasks)
        : lotteryTasks;

    let lotteryAndPrizesDataPromises = [
        getLotteryAndPrizesData(sponsorId, promoId, lotteryInstantUuid),
        getLotteryAndPrizesData(sponsorId, promoId, lotteryParticipantUuid),
        getLotteryAndPrizesData(sponsorId, promoId, lotteryAdminUuid),
        ...Object.entries(lotteryTasksParsed).map(([lotteryUUID, taskCondition]) =>
            getLotteryAndPrizesData(sponsorId, promoId, lotteryUUID, { taskCondition })
        )
    ];
    // 把舊活動的放在 Lottery Participant 的參加就送資料 migrate 到 Lottery Instant
    if (lotteryParticipantUuid && promoStatus === IS_OFFLINE) {
        const prevParticipantLottery = await getLottery(sponsorId, promoId, lotteryParticipantUuid);
        if (prevParticipantLottery.type === JOIN_AND_GET) {
            // 透過打API的方式把 參加就送資料 關聯到後端 Lottery Instant
            await updatePromoSetting(sponsorId, promoId, {
                LotteryInstant: {
                    uuid: lotteryParticipantUuid
                },
                LotteryParticipant: { forceDeleted: ['uuid'] }
            });
            // 把前端舊的參加就送資料送到 redux Lottery Instant 儲存
            [lotteryAndPrizesDataPromises[0], lotteryAndPrizesDataPromises[1]] = [
                lotteryAndPrizesDataPromises[1],
                lotteryAndPrizesDataPromises[0]
            ];
        }
    }

    const [lotteryInstant, lotteryParticipant, lotteryAdmin, ...lotteriesTask] = await Promise.all(
        lotteryAndPrizesDataPromises
    );

    // redux state
    let lotteries = {};
    let prizes = {};

    // [#86ep95r7h] lotteries 因為分類關係，所以需要區分有動畫的獎項 (lotteryParticipant)、抽獎 (lotteryAdmin)、即時講 (lotteryInstant) 和關卡獎 (lotteriesTasks)
    lotteries.lotteryInstant = lotteryInstant.lottery;
    lotteries.lotteryParticipant = lotteryParticipant.lottery;
    lotteries.lotteryAdmin = lotteryAdmin.lottery;
    lotteries.lotteriesTasks = lotteriesTask.reduce((obj, { uuid, lottery }) => {
        obj[uuid] = lottery;
        return obj;
    }, {});

    // prizes 都是用 lotteryUUID 去 mapping 獎品資料，因此無需區分一般獎項和關卡獎
    prizes = [lotteryInstant, lotteryParticipant, lotteryAdmin, ...lotteriesTask].reduce(
        (obj, { uuid, prizes }) => {
            // lotteryParticipant, lotteryAdmin 可能未設定因此沒有 UUID
            if (Boolean(uuid)) {
                obj[uuid] = prizes;
            }
            return obj;
        },
        {}
    );

    return { lotteries, prizes };
}

async function getLotteryAndPrizesData(sponsorId, promoId, lotteryUUID, additionalSettings = {}) {
    if (!lotteryUUID) {
        return {
            lottery: {},
            prizes: []
        };
    }

    const [lottery, prizes] = await Promise.all([
        getLottery(sponsorId, promoId, lotteryUUID),
        getPrizes(sponsorId, promoId, lotteryUUID)
    ]);
    const {
        settings: {
            Lotteries: lotterySettings = {},
            AnimationSettings,
            Tasks: TasksSettings = {}
        } = {},
        ...lotteryRest
    } = lottery;

    return {
        uuid: lotteryUUID,
        lottery: {
            ...lotteryRest,
            ...lotterySettings,
            ...additionalSettings,
            TasksSettings,
            AnimationSettings: parseAnimationSettings(AnimationSettings, lottery?.type)
        },
        prizes: _orderBy(prizes, ['displaySeq'])
    };
}

export function getPromotion(sponsorId, promoId) {
    return fetchPromo(`/services/sponsors/${sponsorId}/promotions/${promoId}`, generateFetchOpts());
}

export function updatePromotion(sponsorId, promoId, body) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}`,
        generateFetchOpts({ method: PATCH, body })
    );
}

export function updatePromotionStatus(
    sponsorId,
    promoId,
    isNeedRelatedPromoUpdate = 0,
    isNeedMGMPromoUpdate = 0,
    body
) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/status?related=${isNeedRelatedPromoUpdate}&mgm_related=${isNeedMGMPromoUpdate}`,
        generateFetchOpts({ method: PATCH, body })
    );
}

export function promoOnlinePreCheck(sponsorId, promoId) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/verification`,
        generateFetchOpts(),
        { passResponse: true }
    );
}

export function getPromoSetting(sponsorId, promoId) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/settings`,
        generateFetchOpts()
    );
}

export function updatePromoSetting(sponsorId, promoId, body) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/settings`,
        generateFetchOpts({ method: PATCH, body })
    );
}

export function getTestcode(sponsorId, promoId) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/previewcode?promotionId=${promoId}`,
        generateFetchOpts()
    );
}

export function clearTestData(sponsorId, promoId) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/test-data`,
        generateFetchOpts({ method: DELETE })
    );
}

function getAccountInfo() {
    return fetchPromo(`/services/admin`, generateFetchOpts());
}

function getAccountRoleInfo(sponsorId) {
    return fetchPromo(`/services/sponsors/${sponsorId}/member/role`, generateFetchOpts());
}

function getSponsor(sponsorId) {
    return fetchPromo(`/services/sponsors/${sponsorId}`, generateFetchOpts());
}

function getSponsorSetting(sponsorId) {
    return fetchPromo(`/services/sponsors/${sponsorId}/settings`, generateFetchOpts());
}

/**
 * 拿所有可用的 PlanKey 和內容
 * totalPromotionLimit, totalEntryLimit, promotionIntervalLimit
 * 這三個 key 的 value 各方案不同，僅參考用
 */
function getSponsorAvailPlanItems(sponsorId) {
    return fetchPromo(`/services/sponsors/${sponsorId}/plans/orders/items`, generateFetchOpts());
}

function getSponsorPlans({ sponsorId }) {
    return fetchPromo(`/services/sponsors/${sponsorId}/plans/orders`, generateFetchOpts());
}

function getPartner() {
    return fetchPromo(`/services/partner`, generateFetchOpts());
}

function getPartnerSetting() {
    return fetchPromo(`/services/partner/settings`, generateFetchOpts());
}

export function createPromoCacheApi(sponsorId, promoId) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/cache`,
        generateFetchOpts({ method: PUT })
    );
}

export function clearPromoRedirectCache(promoUUID) {
    return fetch(`https://${EDITOR_API_FEVER_HOST}/r/promotions/purge/${promoUUID}`, {
        method: GET
    });
}

export function createLotteryPool(sponsorId, promoId) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/lotteries/box`,
        generateFetchOpts({ method: POST })
    );
}

export function getLottery(sponsorId, promoId, lotteryUuid) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/lotteries/${lotteryUuid}`,
        generateFetchOpts()
    );
}

export function updateLotterySetting(sponsorId, promoId, lotteryUuid, body) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/lotteries/${lotteryUuid}/settings`,
        generateFetchOpts({ method: PATCH, body })
    );
}

export function getLotteriesTemplate(sponsorId, promoId) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/lotteries/templates`,
        generateFetchOpts()
    );
}

export function createLottery(sponsorId, promoId, body) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/lotteries`,
        generateFetchOpts({ method: POST, body })
    );
}

// 切換現有 lottery 的 type 使用 lotteryUUID 來找，目前後端用 POST 的方法，跟下面的 `updateLottery` 不一樣
export function updateLotteryType(sponsorId, promoId, lotteryUUID, body) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/lotteries/${lotteryUUID}`,
        generateFetchOpts({ method: POST, body })
    );
}

export function updateLottery(sponsorId, promoId, lotteryId, body) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/lotteries/${lotteryId}`,
        generateFetchOpts({ method: PATCH, body })
    );
}

export function createTaskApi({ sponsorId, promoId, body }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/tasks`,
        generateFetchOpts({
            method: POST,
            body
        })
    );
}

export function copyPrize({ sponsorId, promoId, lotteryUUID, prizeUUID }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/lotteries/${lotteryUUID}/prizes/${prizeUUID}/copy`,
        generateFetchOpts({
            method: POST
        })
    );
}

export function getPrizes(sponsorId, promoId, lotteryId) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/lotteries/${lotteryId}/prizes`,
        generateFetchOpts()
    );
}

export function createPrizeData({ sponsorId, promoId, lotteryId, body }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/lotteries/${lotteryId}/prizes`,
        generateFetchOpts({ method: POST, body })
    );
}

export function updatePrizeData({ sponsorId, promoId, lotteryId, prizeId, body }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/lotteries/${lotteryId}/prizes/${prizeId}`,
        generateFetchOpts({ method: PATCH, body })
    );
}

export function updatePrizeSettings({ sponsorId, promoId, lotteryId, prizeId, body }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/lotteries/${lotteryId}/prizes/${prizeId}/settings`,
        generateFetchOpts({ method: PATCH, body })
    );
}

export function deletePrizeData({ sponsorId, promoId, lotteryId, prizeId }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/lotteries/${lotteryId}/prizes/${prizeId}`,
        generateFetchOpts({ method: DELETE })
    );
}

export function batchUpdatePrizeSettings({ sponsorId, promoId, lotteryId, body }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/lotteries/${lotteryId}/prizes/settings`,
        generateFetchOpts({ method: PATCH, body })
    );
}

export function updatePrizesSeq({ sponsorId, promoId, lotteryId, body }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/lotteries/${lotteryId}/prizes/sequence`,
        generateFetchOpts({ method: PATCH, body })
    );
}

export function createForm(sponsorId, promotionId, templateType, body = {}) {
    return fetchPromo(
        `/sponsors/${sponsorId}/forms`,
        generateFetchOpts({ method: POST, body: { templateType, promotionId, ...body } }),
        { dataKeys: 'items' }
    );
}

export function createSignUpForm(sponsorId, promotionId, body) {
    return createForm(sponsorId, promotionId, formTypes.SIGNUP_FORM, body);
}

export function createPrizeForm(sponsorId, promotionId, body) {
    return createForm(sponsorId, promotionId, formTypes.REDEEM_FORM, body);
}

export function getFormEditorData(sponsorId, formId) {
    return fetchPromo(`/sponsors/${sponsorId}/forms/${formId}`, generateFetchOpts(), {
        dataKeys: 'items'
    });
}

export function updateForm(sponsorId, formId, body) {
    return fetchPromo(
        `/sponsors/${sponsorId}/forms/${formId}`,
        generateFetchOpts({
            method: POST,
            body: {
                ...body,
                options: body.options?.map(option => ({
                    ...option,
                    type: option.type || OPTIONS_TYPE.NORMAL
                }))
            }
        }),
        { dataKeys: 'items' }
    );
}

export function getAwardeeFormList(promotionId, sponsorId) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promotionId}/forms?type=${formTypes.REDEEM_FORM}`,
        generateFetchOpts()
    );
}

export function uploadExcelFile({ sponsorId, promotionId, lotteryUuid, file, prizeUUid }) {
    const formData = new FormData();
    formData.append('prizes', file, file.name);
    const fetchOptions = generateFetchOpts({ method: POST, formData });
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promotionId}/lotteries/${lotteryUuid}/prizes/${prizeUUid}/virtual-numbers/files`,
        fetchOptions
    );
}

export function getBindingCouponList({ sponsorId, promotionId, lotteryUuid, prizeUuid }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promotionId}/lotteries/${lotteryUuid}/prizes/${prizeUuid}/virtual-numbers/files`,
        generateFetchOpts()
    );
}

export function getFileUuidData({
    sponsorId,
    promotionId,
    lotteryUuid,
    prizeUuid,
    fileUuid,
    page = 1
}) {
    const qs = querystring.stringify({ perPage: 10, page });
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promotionId}/lotteries/${lotteryUuid}/prizes/${prizeUuid}/virtual-numbers/files/${fileUuid}?${qs}`,
        generateFetchOpts()
    );
}

export function updatePrizeCoupon({ sponsorId, promoId, lotteryUuid, prizeId, fileUuid }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/lotteries/${lotteryUuid}/prizes/${prizeId}/virtual-numbers/files/${fileUuid}/binding`,
        generateFetchOpts({ method: PATCH })
    );
}

export function deletePrizeCoupons({ sponsorId, promoId, lotteryUuid, prizeId, fileUuids }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/lotteries/${lotteryUuid}/prizes/${prizeId}/virtual-numbers/files/${fileUuids}`,
        generateFetchOpts({ method: DELETE })
    );
}

export function updatePrizeCouponQuantity({ sponsorId, promoId, lotteryUuid, prizeId }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/lotteries/${lotteryUuid}/prizes/${prizeId}/virtual-numbers/sync`,
        generateFetchOpts({ method: PUT })
    );
}

export function uploadEditorImage(file, { type = '', id = '', folderType = '' } = {}) {
    const formData = new FormData();
    formData.append('image', file);
    formData.append('type', type);
    formData.append('uuid', id);
    if (folderType) {
        formData.append('folderType', folderType);
    }
    return fetchPromo(IMG_UPLOAD_API_PATH, generateFetchOpts({ method: POST, formData }));
}

export function downloadFilePasscode({ sponsorId, promoId, passcodeGroupId, columns = [] } = {}) {
    const qs = querystring.stringify(
        columns.reduce((obj, key) => {
            obj[key] = 1;
            return obj;
        }, {})
    );
    return fetchPromoBlob(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/passcode_groups/${passcodeGroupId}/report/download${
            qs ? `?${qs}` : ''
        }`,
        generateFetchOpts()
    );
}

export function getPasscodeGroupList({ sponsorId, promoId } = {}) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/passcode_groups`,
        generateFetchOpts()
    );
}

export function generatePasscodeGroup({
    sponsorId,
    promoId,
    url,
    rows = 1,
    type = passcodeTypes.UNIQUE_CODE,
    maxCounts = 1
} = {}) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/passcode_groups`,
        generateFetchOpts({ method: POST, body: { type, rows, url, maxCounts } })
    );
}

export function generateRealtimePasscode({ sponsorId, promoId, groupId } = {}) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/passcode_groups/${groupId}/passcode`,
        generateFetchOpts({ method: POST })
    );
}

export function deletePasscodeGroup({ sponsorId, promoId, groupId } = {}) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/passcode_groups/${groupId}`,
        generateFetchOpts({ method: DELETE })
    );
}

export function getPasscodeGroupContent({
    sponsorId,
    promoId,
    groupId,
    page = 1,
    perPage = 10
} = {}) {
    const qs = querystring.stringify({ perPage, page });
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/passcode_groups/${groupId}?${qs}`,
        generateFetchOpts()
    );
}

export function bindPromoPlan(sponsorId, promotionId, orderUuid) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promotionId}/plans/orders/bind`,
        generateFetchOpts({ method: PUT, body: { orderUuid } })
    );
}

export function getAvailablePlanListForPromo(sponsorId, promotionId) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promotionId}/plans/orders`,
        generateFetchOpts()
    );
}

export function updatePromoOptionApi({ sponsorId, promotionId, body }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promotionId}/options`,
        generateFetchOpts({ method: POST, body })
    );
}
export function deletePromoOptionApi({ sponsorId, promotionId, optionUuids }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promotionId}/options`,
        generateFetchOpts({ method: DELETE, body: { uuids: optionUuids } })
    );
}
export function getPromoOptionListApi({ sponsorId, promotionId }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promotionId}/options`,
        generateFetchOpts()
    );
}

export function getUploadTypeApi({ sponsorId, promoId, lotteryUUID, prizeUUID, fileUUid }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/lotteries/${lotteryUUID}/prizes/${prizeUUID}/virtual-numbers/files/${fileUUid}/type`,
        generateFetchOpts()
    );
}

export function createPromoWithTemplateUuid(sponsorId, requestBody) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions`,
        generateFetchOpts({
            method: POST,
            body: requestBody
        })
    );
}

/**
 * @method getPromoTemplates 取得活動模組 Template 資料
 * @param  {string} sponsorId
 * @return {promise}
 */
export function getPromoTemplates(sponsorId) {
    return fetchPromo(`/services/sponsors/${sponsorId}/promotions/templates`, generateFetchOpts());
}

export function uploadMediaImage({
    sponsorId,
    promoId,
    file,
    compressType,
    seq,
    mediaUrl,
    mediaSource
}) {
    const formData = new FormData();

    // 傳遞 img URL 讓後端去抓該 URL img 再存到 S3 避免有些圖 CORS 前端無法轉為 blob 傳給後端
    if (Boolean(mediaUrl) && Boolean(mediaSource)) {
        formData.append('mediaUrl', mediaUrl);
        formData.append('mediaSource', mediaSource);
    } else {
        formData.append('image', file, file.name);
    }
    formData.append('compressType', compressType);
    formData.append('seq', seq);
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/media`,
        generateFetchOpts({ method: POST, formData })
    );
}

export function createMedia({ sponsorId, promoId, requestBody }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/media-libraries`,
        generateFetchOpts({ method: POST, body: requestBody })
    );
}

export function getMediaList({ sponsorId, promoId, qs }) {
    const parsedQs = querystring.stringify(qs);

    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/entries${
            parsedQs ? `?${parsedQs}` : ''
        }`,
        generateFetchOpts()
    );
}

export function getEntriesCountsInfo({ sponsorId, promoId }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/entries/counts`,
        generateFetchOpts()
    );
}

export function getMediaInfo({ sponsorId, promoId, entryId }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/entries/${entryId}/record`,
        generateFetchOpts()
    );
}

export function deleteMedia({ sponsorId, promoId, entryId, mediaLibraryUuid }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/entries/${entryId}/media-libraries/${mediaLibraryUuid}`,
        generateFetchOpts({ method: DELETE })
    );
}

export function updateMedia({ sponsorId, promoId, entryId, mediaLibraryUuid, requestBody }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/entries/${entryId}/media-libraries/${mediaLibraryUuid}`,
        generateFetchOpts({ method: PATCH, body: requestBody })
    );
}

export function recoverMedias({ sponsorId, promoId, requestBody }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/media-libraries`,
        generateFetchOpts({ method: PUT, body: requestBody })
    );
}

function getPromoTaskListApi({ sponsorId, promoId }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/tasks`,
        generateFetchOpts()
    );
}

export function getLotteryTaskListApi({ sponsorId, promoId, lotteryUUID }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/lotteries/${lotteryUUID}/tasks`,
        generateFetchOpts()
    );
}

export function updateLotteryTaskListApi({ sponsorId, promoId, taskUuid, body }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/tasks/${taskUuid}/relation`,
        generateFetchOpts({
            method: PUT,
            body
        })
    );
}

export function updatePromoTaskApi({ sponsorId, promoId, taskUUID, body }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/tasks/${taskUUID}`,
        generateFetchOpts({ method: PATCH, body })
    );
}

export function updatePromoTaskSequenceApi({ sponsorId, promoId, uuidsSequence }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/tasks/sequence`,
        generateFetchOpts({
            method: PUT,
            body: {
                tasks: uuidsSequence.reduce((payload, uuid, index) => {
                    payload[uuid] = index + 1;
                    return payload;
                }, {})
            }
        })
    );
}

export function deletePromoTaskApi({ sponsorId, promoId, taskUUID, body }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/tasks/${taskUUID}`,
        generateFetchOpts({
            method: DELETE,
            body
        })
    );
}

/**
 * @method updateAllPromoTaskSettingApi 同步更新 Task Setting
 * https://app.clickup.com/t/86epr4j2f
 * @param  {string} sponsorId
 * @param  {string} promoId
 * @param  {string[]} targetTaskUuids
 * @return  {promise}
 */
export function updateAllPromoTaskSettingApi({
    sponsorId,
    promoId,
    updateByTaskUUIDs = taskSyncTypes.byAllTasks,
    taskType = taskTypes.PROMOTION,
    taskUUIDs = [],
    settings = {}
}) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/tasks/settings/sync`,
        generateFetchOpts({
            method: PATCH,
            body: {
                selectAll: updateByTaskUUIDs,
                type: taskType,
                targetTaskUuid: taskUUIDs,
                settings
            }
        })
    ).then(res => res);
}

export function subscribeIgWebhook({ sponsorId, promoId, isPublish, subscribeMode }) {
    const data = isPublish
        ? { promotionId: promoId, 'subscribe-mode': subscribeMode, 'is-publish': isPublish }
        : { promotionId: promoId, 'subscribe-mode': subscribeMode };
    return fetchPromo(
        `/services/sponsors/${sponsorId}/ig-webhook/subscription`,
        generateFetchOpts({
            method: POST,
            body: data
        })
    );
}

/**
 * @method transformShortLinkApi 註冊並取得短網址 api
 * @param  {string} sponsorId
 * @param  {string} promoId
 * @return  {promise}
 */
export function transformShortLinkApi(sponsorId, promoId, url) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/short-link
    `,
        generateFetchOpts({ method: POST, body: { type: 2, url } })
    );
}

/**
 * @method updateAsyncLotterySetting 同步 lottery setting API
 * https://app.clickup.com/t/86epjf6pu
 * @param  {string} sponsorId
 * @param  {string} promoId
 * @return  {promise}
 */
export function updateAsyncLotterySetting(sponsorId, promoId, body) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/lotteries/settings/sync`,
        generateFetchOpts({ method: PATCH, body })
    );
}

/**
 * @method getPromosListApi 取得 Promotion List
 * https://app.clickup.com/t/86epmr6j1
 * @param  {string} sponsorId
 * @param  {Number} perPage 每一頁撈幾筆資料
 * @param  {Number} page 要撈第幾頁的資料
 * @param  {string} searchKey 用搜尋關鍵字過濾活動
 * @param  {string} additionalQs
 * @param  {string[]} type 過濾要加入的 promo type
 * @param  {string[]} excludeType 過濾要排除的 promo type
 * @param  {Number[]} loginMethod 要過濾的 login type
 * @return  {promise}
 */
export function getPromosListApi(
    sponsorId,
    { perPage, page, searchKey, additionalQs, type = [], excludeType = [], loginMethod = [] }
) {
    const includePromoTypesQs = type.join(',');
    const excludePromoTypesQs = excludeType.join(',');
    const promoLoginMethodQs = loginMethod.join(',');
    const qs = querystring.stringify({
        perPage,
        page,
        search: searchKey || undefined,
        'filter[type]': includePromoTypesQs || undefined,
        'filter[excludeType]': excludePromoTypesQs || undefined,
        loginMethod: promoLoginMethodQs || undefined
    });
    const composedQs = qs ? `${qs}${additionalQs ? `&${additionalQs}` : ''}` : additionalQs;

    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions${composedQs ? `?${composedQs}` : ''}`,
        generateFetchOpts()
    ).then(res => res);
}
/**
 * @method getPromoAiExtensionsApi 取得 Promotion AI 擴充功能設定
 * @description https://app.clickup.com/t/86ettgjum
 * @param  {string} sponsorId
 * @param  {string} promoId
 * @return  {promise}
 */
export function getPromoAiExtensionsApi(sponsorId, promoId) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/ai-extensions`,
        generateFetchOpts()
    );
}

/**
 * @method getAiExtensionConfigsApi 取得 AI 擴充功能設定
 * @description https://app.clickup.com/t/86etv03pe
 * @return {promise}
 */
export function getAiExtensionConfigsApi() {
    return fetchPromo('/services/ai-extension/configs', generateFetchOpts());
}

/**
 * @method updateAIExtensionSettingsApi  更新 AI Extension Setting API
 * @description https://app.clickup.com/t/86ett1dbg
 * @param  {string} sponsorId
 * @param  {string} promoId
 * @param  {string} aiExtensionUuid
 * @param  {object} settings
 * @return {promise}
 */
export function updateAIExtensionSettingsApi(sponsorId, promoId, aiExtensionUuid, settings) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/ai-extensions/${aiExtensionUuid}/settings`,
        generateFetchOpts({
            method: 'PATCH',
            body: settings
        })
    );
}

/**
 * @method createAIExtensionsApi 建立 AI Extension API
 * @description https://app.clickup.com/t/86ett1d7b
 * @param  {string} sponsorId
 * @param  {string} promoId
 * @param  {object} payload
 * @return {promise}
 */
export function createAIExtensionsApi(sponsorId, promoId, payload) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/ai-extensions`,
        generateFetchOpts({
            method: 'POST',
            body: payload
        })
    );
}

/**
 * @method updateAIExtensionApi 更新 AI Extension API
 * @description https://app.clickup.com/t/86ett1d9e
 * @param  {string} sponsorId
 * @param  {string} promoId
 * @param  {string} aiExtensionUuid
 * @param  {object} payload
 * @return {promise}
 */
export function updateAIExtensionApi(sponsorId, promoId, aiExtensionUuid, payload) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/promotions/${promoId}/ai-extensions/${aiExtensionUuid}`,
        generateFetchOpts({
            method: 'PUT',
            body: payload
        })
    );
}

export const THIRD_PARTY_LEVEL_TYPE = {
    SPONSOR: 'sponsor',
    PARTNER: 'partner'
};

/**
 * 獲取第三方 API 設定
 * @param {Object} params - 包含以下屬性的參數物件
 * @param {string} params.sponsorId - sponsor Id
 * @param {'sponsor' | 'partner'} params.level - 層級
 * @param {string} params.levelUuid - 層級對應的 Uuid
 * @returns {Promise} - 返回包含第三方平台設定的 Promise
 */
export function getThirdPartyApi({ sponsorId, level, levelUuid }) {
    return fetchPromo(
        `/services/sponsors/${sponsorId}/third-party-platforms/level-settings?level=${level}&levelUuid=${levelUuid}`,
        generateFetchOpts({ method: GET }),
        { dataKeys: 'data' }
    );
}
